import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';

import { BLACK, FEED_BACK, PRIMARY_PURPLE, RED } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';

import { BYTES_PER_MB, DEFAULT_MAX_FILE_SIZE_MB, FILE_UPLOAD_STATUS } from './fileUploadConstants';

const getBorderColor = (status, disabled) => {
  if (disabled) return BLACK[20];
  if (status === FILE_UPLOAD_STATUS.DRAG_ACTIVE) return PRIMARY_PURPLE[60];
  if (status === FILE_UPLOAD_STATUS.ERROR) return RED[70];
  if (status === FILE_UPLOAD_STATUS.ACCEPTED) return FEED_BACK.GREEN_70;
  return BLACK[30];
};

const getBackgroundColor = (status, disabled) => {
  if (disabled) return BLACK[10];
  if (status === FILE_UPLOAD_STATUS.DRAG_ACTIVE) return PRIMARY_PURPLE[10];
  if (status === FILE_UPLOAD_STATUS.ERROR) return RED[0];
  if (status === FILE_UPLOAD_STATUS.ACCEPTED) return FEED_BACK.GREEN_00;
  return BLACK[0];
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < BYTES_PER_MB) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
};

const formatAcceptedTypes = (accept) => {
  if (!accept) return '';
  return Object.keys(accept)
    .flatMap((mime) => accept[mime])
    .filter(Boolean)
    .join(', ');
};

const getTotalSize = (files) => files.reduce((sum, f) => sum + (f.size || 0), 0);

const getCurrentStatus = (isDragActive, uploadStatus) => {
  if (uploadStatus === FILE_UPLOAD_STATUS.ERROR) return FILE_UPLOAD_STATUS.ERROR;
  if (isDragActive) return FILE_UPLOAD_STATUS.DRAG_ACTIVE;
  return uploadStatus;
};

// NOTE: LanguageConverterV2 wraps react-intl's `useIntl` hook internally, so every call to
// it must happen unconditionally during render (same order/count every time). It must never
// be called from inside event handlers (e.g. react-dropzone's onDrop/onDropRejected), since
// those run outside React's render phase and calling a hook there throws "Invalid hook call"
// at runtime, silently aborting the handler before any state updates (like showing the error)
// can run. That's why rejection reasons could compute correctly in tests (where this helper is
// mocked as a plain function) but never actually appear in the browser. To stay safe, every
// possible translated string is precomputed below, unconditionally, and handlers only ever
// store plain error codes/strings in state.
const getRejectionMessageForCode = (code, messages) => {
  if (code === 'file-too-large') return messages.sizeTooLarge;
  if (code === 'file-too-small') return messages.empty;
  if (code === 'file-invalid-type') return messages.invalidType;
  if (code === 'too-many-files') return messages.tooManyFiles;
  return messages.generic;
};

const FileUpload = ({
  accept,
  maxSize,
  maxFiles,
  onFileAccepted,
  onFileRejected,
  onReset,
  enableCameraCapture = false,
  multiple = false,
  disabled = false,
  dataTestId,
  sx = {},
}) => {
  const [status, setStatus] = useState(FILE_UPLOAD_STATUS.IDLE);
  const [errorCode, setErrorCode] = useState(null);
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const isMobile = useMediaQuery('(max-width:768px)');

  const maxSizeBytes = (maxSize || DEFAULT_MAX_FILE_SIZE_MB) * BYTES_PER_MB;
  const minSizeBytes = 1;

  const handleDrop = useCallback(
    (accepted, rejected) => {
      if (rejected.length > 0) {
        const { errors } = rejected[0];
        const firstError = errors[0];

        setErrorCode(firstError.code);
        setStatus(FILE_UPLOAD_STATUS.ERROR);
        setAcceptedFiles([]);

        if (onFileRejected) {
          onFileRejected(rejected);
        }
        return;
      }

      if (accepted.length > 0) {
        setStatus(FILE_UPLOAD_STATUS.ACCEPTED);
        setErrorCode(null);
        setAcceptedFiles(accepted);

        if (onFileAccepted) {
          onFileAccepted(multiple ? accepted : accepted[0]);
        }
      }
    },
    [multiple, onFileAccepted, onFileRejected]
  );

  const handleReset = useCallback(() => {
    setStatus(FILE_UPLOAD_STATUS.IDLE);
    setErrorCode(null);
    setAcceptedFiles([]);

    if (onReset) {
      onReset();
    }
  }, [onReset]);

  const dropzoneOptions = {
    onDrop: handleDrop,
    accept,
    maxSize: maxSizeBytes,
    minSize: minSizeBytes,
    multiple,
    disabled,
    noClick: false,
    noKeyboard: false,
  };

  if (maxFiles != null) {
    dropzoneOptions.maxFiles = maxFiles;
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneOptions);

  const inputProps = getInputProps();
  if (enableCameraCapture && isMobile) {
    inputProps.capture = 'environment';
  }

  const currentStatus = getCurrentStatus(isDragActive, status);
  const hasAcceptedFiles = acceptedFiles.length > 0;
  const isMultipleAccepted = acceptedFiles.length > 1;
  const hasDropError = status === FILE_UPLOAD_STATUS.ERROR && !!errorCode;

  // Precomputed unconditionally, every render, so LanguageConverterV2 (which wraps the
  // useIntl hook) is always called the same number of times regardless of which branch
  // below actually gets displayed. See note on getRejectionMessageForCode above.
  const allowedTypesText = formatAcceptedTypes(accept);
  const rejectionMessages = {
    sizeTooLarge: LanguageConverterV2('error.fileUpload.sizeTooLarge', {
      maxSize: maxSize || DEFAULT_MAX_FILE_SIZE_MB,
    }),
    empty: LanguageConverterV2('error.fileUpload.empty'),
    invalidType: allowedTypesText
      ? LanguageConverterV2('error.fileUpload.invalidType', { types: allowedTypesText })
      : LanguageConverterV2('error.fileUpload.invalidTypeGeneric'),
    tooManyFiles: LanguageConverterV2('error.fileUpload.tooManyFiles', { maxFiles }),
    generic: LanguageConverterV2('error.fileUpload.generic'),
  };
  const errorMessage = hasDropError ? getRejectionMessageForCode(errorCode, rejectionMessages) : '';

  const dropHereText = LanguageConverterV2('fileUpload.dropHere');
  const dragOrClickText = LanguageConverterV2('fileUpload.dragOrClick');
  const orBrowseText = LanguageConverterV2('fileUpload.orBrowse');
  const changeFileText = LanguageConverterV2('fileUpload.changeFile');
  const filesSelectedText = LanguageConverterV2('fileUpload.filesSelected', {
    count: acceptedFiles.length,
  });
  const sizeText = LanguageConverterV2('fileUpload.maxSize', {
    size: maxSize || DEFAULT_MAX_FILE_SIZE_MB,
  });
  const acceptedFormatsText = allowedTypesText
    ? LanguageConverterV2('fileUpload.acceptedFormats', { types: allowedTypesText })
    : '';

  const showHint = accept || maxSize;

  const renderDropzoneContent = () => {
    if (hasDropError) {
      return (
        <>
          <ErrorOutlineOutlinedIcon sx={{ fontSize: 40, color: RED[70], mb: 1 }} />
          <Typography
            role="alert"
            data-testid={dataTestId ? `${dataTestId}-error` : undefined}
            sx={{
              fontFamily: PRIMARY_FONT[500],
              fontSize: '14px',
              color: RED[80],
              textAlign: 'center',
              px: 1,
            }}
          >
            {errorMessage}
          </Typography>
          <Typography
            sx={{
              fontFamily: PRIMARY_FONT[300],
              fontSize: '12px',
              color: BLACK[50],
              mt: 0.5,
              textAlign: 'center',
            }}
          >
            {dragOrClickText}
          </Typography>
        </>
      );
    }

    if (currentStatus === FILE_UPLOAD_STATUS.ACCEPTED && hasAcceptedFiles) {
      return (
        <>
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 40, color: FEED_BACK.GREEN_70, mb: 1 }} />
          {isMultipleAccepted ? (
            <>
              <Typography
                sx={{
                  fontFamily: PRIMARY_FONT[500],
                  fontSize: '14px',
                  color: FEED_BACK.GREEN_80,
                  textAlign: 'center',
                }}
              >
                {filesSelectedText}
              </Typography>
              <Typography
                sx={{
                  fontFamily: PRIMARY_FONT[300],
                  fontSize: '12px',
                  color: BLACK[50],
                  mt: 0.5,
                }}
              >
                {formatFileSize(getTotalSize(acceptedFiles))}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: PRIMARY_FONT[500],
                  fontSize: '14px',
                  color: FEED_BACK.GREEN_80,
                  textAlign: 'center',
                  wordBreak: 'break-word',
                }}
              >
                {acceptedFiles[0].name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: PRIMARY_FONT[300],
                  fontSize: '12px',
                  color: BLACK[50],
                  mt: 0.5,
                }}
              >
                {formatFileSize(acceptedFiles[0].size)}
              </Typography>
            </>
          )}
        </>
      );
    }

    return (
      <>
        <CloudUploadOutlinedIcon
          sx={{
            fontSize: 40,
            color:
              currentStatus === FILE_UPLOAD_STATUS.DRAG_ACTIVE ? PRIMARY_PURPLE[60] : BLACK[40],
            mb: 1,
          }}
        />
        <Typography
          sx={{
            fontFamily: PRIMARY_FONT[500],
            fontSize: '14px',
            color: disabled ? BLACK[40] : BLACK[80],
            textAlign: 'center',
          }}
        >
          {isDragActive ? dropHereText : dragOrClickText}
        </Typography>
        {!isMobile && (
          <Typography
            sx={{
              fontFamily: PRIMARY_FONT[300],
              fontSize: '12px',
              color: BLACK[40],
              mt: 0.5,
            }}
          >
            {orBrowseText}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Box sx={{ width: '100%', ...sx }} data-testid={dataTestId}>
      <Box
        {...getRootProps()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '24px 16px' : '32px 24px',
          border: `2px dashed ${getBorderColor(currentStatus, disabled)}`,
          borderRadius: '8px',
          backgroundColor: getBackgroundColor(currentStatus, disabled),
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s ease',
          minHeight: isMobile ? '120px' : '150px',
          ...(!disabled &&
            currentStatus !== FILE_UPLOAD_STATUS.ERROR && {
              '&:hover': {
                borderColor: PRIMARY_PURPLE[50],
                backgroundColor: PRIMARY_PURPLE[10],
              },
            }),
          '&:focus-visible': {
            outline: `2px solid ${PRIMARY_PURPLE[60]}`,
            outlineOffset: '2px',
          },
        }}
      >
        <input {...inputProps} data-testid={dataTestId ? `${dataTestId}-input` : undefined} />

        {renderDropzoneContent()}
      </Box>

      {currentStatus === FILE_UPLOAD_STATUS.ACCEPTED && hasAcceptedFiles && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleReset();
            }}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-reset` : undefined}
            sx={{
              fontFamily: PRIMARY_FONT[500],
              fontSize: '13px',
              color: PRIMARY_PURPLE[70],
              textTransform: 'none',
              minWidth: 'auto',
              padding: '2px 8px',
            }}
          >
            {changeFileText}
          </Button>
        </Box>
      )}

      {showHint && (
        <Typography
          sx={{
            fontFamily: PRIMARY_FONT[300],
            fontSize: '11px',
            color: BLACK[40],
            mt: 0.5,
            px: 0.5,
          }}
        >
          {acceptedFormatsText ? `${acceptedFormatsText} · ${sizeText}` : sizeText}
        </Typography>
      )}
    </Box>
  );
};

FileUpload.propTypes = {
  accept: PropTypes.shape({}),
  maxSize: PropTypes.number,
  maxFiles: PropTypes.number,
  onFileAccepted: PropTypes.func.isRequired,
  onFileRejected: PropTypes.func,
  onReset: PropTypes.func,
  enableCameraCapture: PropTypes.bool,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
  sx: PropTypes.shape({}),
};

export default FileUpload;
